/**
 * The one table in the console.
 *
 * Every tabular view goes through this component, so column resizing exists once and
 * behaves the same everywhere: drag the divider in a header, or focus it and use the
 * arrow keys. Widths are remembered per table, so a reviewer who widens "what the check
 * found" finds it wide again tomorrow.
 *
 * Deliberately not a table library. Fixed layout plus a `<colgroup>` is the whole
 * mechanism; anything more would be a dependency to carry for behaviour we can see.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

export interface Column<T> {
  /** Stable id — the persistence key, so renaming a header keeps the saved width. */
  key: string;
  header: ReactNode;
  /** Starting width in px. Every column has one: fixed layout needs real numbers. */
  width: number;
  align?: "start" | "end";
  /** Extra classes for the body cell, e.g. `font-mono`. */
  cellClass?: string;
  /** Default true. The selection column sets false — a checkbox column a user can drag
   *  to 900px is a bug with a handle on it. */
  resizable?: boolean;
  render: (row: T) => ReactNode;
}

export interface Selection<T> {
  selected: ReadonlySet<string>;
  onChange: (next: ReadonlySet<string>) => void;
  /** Rows the caller will not allow selecting, e.g. one already being deleted. */
  disabled?: (row: T) => boolean;
}

export const SELECT_COLUMN_KEY = "__select";

const MIN_WIDTH = 56;
const MAX_WIDTH = 900;
const STEP = 16;

function storageKey(table: string): string {
  return `alt.cols.${table}`;
}

function readWidths(table: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(storageKey(table));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    // Only keep sane numbers: a corrupt entry must not collapse a column to nothing.
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        (entry): entry is [string, number] =>
          typeof entry[1] === "number" && Number.isFinite(entry[1]) && entry[1] >= MIN_WIDTH,
      ),
    );
  } catch {
    return {};
  }
}

function clamp(width: number): number {
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(width)));
}

export function DataTable<T>({
  table,
  columns,
  rows,
  rowKey,
  onRowClick,
  fill = false,
  selection,
  loading = false,
}: {
  /** Identifies this table for width persistence. Stable across renames. */
  table: string;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  onRowClick?: (row: T) => void;
  /** Claim the parent's remaining height and scroll the body, header pinned. */
  fill?: boolean;
  selection?: Selection<T>;
  /** A skeleton with this table's own column widths. Loading cannot do this at the call
   *  site: the widths live in DataTable's private state and localStorage, and a spinner
   *  makes the layout jump when rows arrive. */
  loading?: boolean;
}) {
  const [widths, setWidths] = useState<Record<string, number>>(() => readWidths(table));
  const dragging = useRef<{ key: string; startX: number; startWidth: number } | null>(null);

  const widthOf = useCallback(
    (column: Column<T>) => widths[column.key] ?? column.width,
    [widths],
  );

  const persist = useCallback(
    (next: Record<string, number>) => {
      setWidths(next);
      try {
        localStorage.setItem(storageKey(table), JSON.stringify(next));
      } catch {
        /* the resize still applies for this session */
      }
    },
    [table],
  );

  // Pointer handlers live on the window so a fast drag that leaves the header still works.
  useEffect(() => {
    function move(event: PointerEvent) {
      const drag = dragging.current;
      if (!drag) return;
      event.preventDefault();
      setWidths((current) => ({
        ...current,
        [drag.key]: clamp(drag.startWidth + (event.clientX - drag.startX)),
      }));
    }
    function up() {
      if (!dragging.current) return;
      dragging.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      // Read back through the setter so the final width is what actually landed.
      setWidths((current) => {
        try {
          localStorage.setItem(storageKey(table), JSON.stringify(current));
        } catch {
          /* nothing to do */
        }
        return current;
      });
    }
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [table]);

  function startDrag(event: React.PointerEvent, column: Column<T>) {
    event.preventDefault();
    dragging.current = { key: column.key, startX: event.clientX, startWidth: widthOf(column) };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }

  const lastToggled = useRef<number | null>(null);

  const keys = rows.map((row, index) => rowKey(row, index));
  // Keyed by rowKey, and indexed against `rows` rather than a filtered copy — filtering
  // first renumbers the indices and would key a disabled row's neighbour.
  const selectableKeys = selection
    ? keys.filter((_, index) => !selection.disabled?.(rows[index]!))
    : [];
  const allSelected =
    selection !== undefined &&
    selectableKeys.length > 0 &&
    selectableKeys.every((key) => selection.selected.has(key));

  function toggle(index: number, shift: boolean) {
    if (!selection) return;
    const next = new Set(selection.selected);
    const from = shift && lastToggled.current !== null ? lastToggled.current : index;
    const [start, end] = from <= index ? [from, index] : [index, from];
    const turningOn = !next.has(keys[index]!);
    for (let at = start; at <= end; at += 1) {
      const key = keys[at]!;
      const row = rows[at]!;
      if (selection.disabled?.(row)) continue;
      if (turningOn) next.add(key);
      else next.delete(key);
    }
    lastToggled.current = index;
    selection.onChange(next);
  }

  // 56, not a smaller number that reads better on paper: MIN_WIDTH is 56 and clamp()
  // floors every width there, so a narrower constant renders at 56 anyway while being
  // discarded by readWidths' `>= MIN_WIDTH` filter on reload.
  const SELECT_COLUMN: Column<T> = {
    key: SELECT_COLUMN_KEY,
    width: MIN_WIDTH,
    resizable: false,
    header: (
      <input
        type="checkbox"
        aria-label="Select every row"
        checked={allSelected}
        ref={(node) => {
          if (node) {
            node.indeterminate =
              !allSelected && (selection?.selected.size ?? 0) > 0;
          }
        }}
        onChange={() => {
          if (!selection) return;
          selection.onChange(allSelected ? new Set() : new Set(selectableKeys));
        }}
      />
    ),
    render: () => null, // replaced per row below
  };

  const cols = selection ? [SELECT_COLUMN, ...columns] : columns;

  function nudge(event: React.KeyboardEvent, column: Column<T>) {
    const delta = event.key === "ArrowLeft" ? -STEP : event.key === "ArrowRight" ? STEP : 0;
    if (delta === 0) return;
    event.preventDefault();
    persist({ ...widths, [column.key]: clamp(widthOf(column) + delta) });
  }

  return (
    <div className={fill ? "min-h-0 flex-1 overflow-auto" : "overflow-x-auto"}>
      <table className="w-full table-fixed border-collapse text-[13px]">
        <colgroup>
          {cols.map((column) => (
            <col key={column.key} style={{ width: `${widthOf(column)}px` }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {cols.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={`eyebrow sticky top-0 z-[1] relative border-b border-hair bg-surface px-4 py-2.5 whitespace-nowrap ${
                  column.align === "end" ? "text-end" : "text-start"
                }`}
              >
                {column.header}
                {column.resizable !== false && (
                <span
                  role="separator"
                  aria-orientation="vertical"
                  aria-label={`Resize column`}
                  tabIndex={0}
                  onPointerDown={(event) => startDrag(event, column)}
                  onKeyDown={(event) => nudge(event, column)}
                  onDoubleClick={() => {
                    const { [column.key]: _dropped, ...rest } = widths;
                    persist(rest);
                  }}
                  title="Drag to resize · double-click to reset · arrow keys to nudge"
                  className="absolute inset-y-0 end-0 z-10 w-2 translate-x-1 cursor-col-resize select-none before:absolute before:inset-y-1 before:start-1/2 before:w-px before:bg-hair before:content-[''] hover:before:bg-gold focus-visible:before:bg-gold"
                />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody data-testid={loading ? "datatable-skeleton" : undefined}>
          {loading
            ? Array.from({ length: 8 }, (_, row) => (
                <tr key={`skeleton-${row}`}>
                  {cols.map((column) => (
                    <td key={column.key} className="border-b border-hair-soft px-4 py-2.5">
                      <span className="block h-3 w-full bg-hair-soft" />
                    </td>
                  ))}
                </tr>
              ))
            : rows.map((row, index) => (
                <tr
                  key={keys[index]}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  aria-selected={selection?.selected.has(keys[index]!) ?? undefined}
                  className={`transition-colors duration-150 hover:bg-hair-soft ${
                    onRowClick ? "cursor-pointer" : ""
                  } ${selection?.selected.has(keys[index]!) ? "bg-gold-wash" : ""}`}
                >
              {cols.map((column) =>
                column.key === SELECT_COLUMN_KEY ? (
                  <td
                    key={column.key}
                    className="overflow-hidden border-b border-hair-soft px-4 py-2.5 align-middle"
                    // <tr onClick> wraps every <td>: without this, checking a box on the
                    // documents list navigates to the document.
                    onClick={(event) => event.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      aria-label={`Select row ${index + 1}`}
                      checked={selection?.selected.has(keys[index]!) ?? false}
                      disabled={selection?.disabled?.(row) ?? false}
                      // shiftKey lives on the CLICK event, not onChange's — a checkbox's
                      // onChange fires from a synthetic "input" event whose nativeEvent is
                      // not reliably a MouseEvent, so `.shiftKey` there is not trustworthy
                      // across browsers/testing-library. onClick's MouseEvent always
                      // carries it. Toggling here (not in onChange) also means this is
                      // the one place that needs stopPropagation, since the click already
                      // fully drives the state change via `toggle`.
                      onClick={(event) => {
                        event.stopPropagation();
                        toggle(index, event.shiftKey);
                      }}
                      onChange={() => {}}
                    />
                  </td>
                ) : (
                  <td
                    key={column.key}
                    // overflow-hidden, because `table-fixed` honours the column width
                    // for layout and then lets content paint straight over the next
                    // column. A long unbroken filename does exactly that.
                    className={`overflow-hidden border-b border-hair-soft px-4 py-2.5 align-middle ${
                      column.align === "end" ? "text-end" : ""
                    } ${column.cellClass ?? ""}`}
                  >
                    {column.render(row)}
                  </td>
                ),
              )}
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
}
